// The walkable volume.
//
// Geometry and collision are built from the SAME layout data (city/layout.js), never authored
// twice. A collider hand-placed to match a wall is a second source of truth that silently drifts;
// here the wall and its collider come out of one record, so they cannot disagree.

import { WORLD } from './config.js';

/** Axis-aligned box you cannot walk through. Stored as flat numbers -- there are a lot of these. */
export class World {
  constructor() {
    this.solids = [];   // {x0,x1,z0,z1,y0,y1, tag}
    this.floors = [];   // {x0,x1,z0,z1,y, tag} -- horizontal walkable surfaces above street level
    this.ramps = [];    // {x0,x1,z0,z1,yLo,yHi, axis:'x'|'z', dir:+1|-1, tag}
  }

  solid(x0, x1, z0, z1, y0, y1, tag = '') {
    this.solids.push({
      x0: Math.min(x0, x1), x1: Math.max(x0, x1),
      z0: Math.min(z0, z1), z1: Math.max(z0, z1),
      y0: Math.min(y0, y1), y1: Math.max(y0, y1), tag,
    });
    return this;
  }

  floor(x0, x1, z0, z1, y, tag = '') {
    this.floors.push({
      x0: Math.min(x0, x1), x1: Math.max(x0, x1),
      z0: Math.min(z0, z1), z1: Math.max(z0, z1), y, tag,
    });
    return this;
  }

  /** A stair flight. `axis` is the direction of travel; height goes yLo->yHi along +axis if dir=+1. */
  ramp(x0, x1, z0, z1, yLo, yHi, axis, dir = 1, tag = '') {
    this.ramps.push({
      x0: Math.min(x0, x1), x1: Math.max(x0, x1),
      z0: Math.min(z0, z1), z1: Math.max(z0, z1),
      yLo, yHi, axis, dir, tag,
    });
    return this;
  }

  // -------------------------------------------------------------------------
  // Street ground. The roadway is cambered and the sidewalks are kerbed; this is
  // the analytic base surface everything else sits on top of.
  // -------------------------------------------------------------------------
  streetGround(x, z) {
    if (z < WORLD.STREET_Z0 - 2 || z > WORLD.STREET_Z1 + 2) return 0;
    const ax = Math.abs(x);
    if (ax <= WORLD.ROAD_HALF) {
      // Camber: crown at the centreline falling to the gutter at the kerb.
      const t = ax / WORLD.ROAD_HALF;
      return WORLD.ROAD_CROWN * (1 - t * t);
    }
    if (ax <= WORLD.FACE) return WORLD.KERB_H;
    return WORLD.KERB_H;
  }

  /**
   * Highest walkable surface at (x,z) that the feet can reach from `fromY`.
   * `fromY` is the current foot height; anything more than STEP_UP above it is not reachable, which
   * is what stops the player from teleporting onto the walkway from the street below it.
   */
  groundAt(x, z, fromY = 0) {
    let best = this.streetGround(x, z);
    const reach = fromY + WORLD.STEP_UP;

    for (const f of this.floors) {
      if (x < f.x0 || x > f.x1 || z < f.z0 || z > f.z1) continue;
      if (f.y > best && f.y <= reach) best = f.y;
    }
    for (const r of this.ramps) {
      if (x < r.x0 || x > r.x1 || z < r.z0 || z > r.z1) continue;
      const t = r.axis === 'x'
        ? (x - r.x0) / Math.max(1e-6, r.x1 - r.x0)
        : (z - r.z0) / Math.max(1e-6, r.z1 - r.z0);
      const u = r.dir > 0 ? t : 1 - t;
      const y = r.yLo + (r.yHi - r.yLo) * u;
      if (y > best && y <= reach) best = y;
    }
    return best;
  }

  /**
   * Push a circle of radius `rad` at (x,z), standing with feet at `footY`, out of every solid it
   * overlaps. Returns the corrected [x,z].
   *
   * Vertical filtering matters: the walkway deck is a solid to someone on the street (you cannot
   * walk through its underside supports) but the deck itself must not block the person standing ON
   * it. Solids carry a y-span and only block when the body actually spans it.
   */
  resolve(x, z, rad, footY, headY) {
    // Two passes: resolving against one box can push you into another.
    for (let pass = 0; pass < 3; pass++) {
      let moved = false;
      for (const s of this.solids) {
        if (headY <= s.y0 || footY >= s.y1) continue; // body is entirely above or below this box

        // Nearest point on the box (in XZ) to the circle centre.
        const nx = Math.max(s.x0, Math.min(x, s.x1));
        const nz = Math.max(s.z0, Math.min(z, s.z1));
        const dx = x - nx, dz = z - nz;
        const d2 = dx * dx + dz * dz;

        if (d2 > rad * rad) continue;

        if (d2 > 1e-9) {
          const d = Math.sqrt(d2);
          x = nx + (dx / d) * rad;
          z = nz + (dz / d) * rad;
        } else {
          // Centre is inside the box: eject along the shallowest face.
          const dxl = x - s.x0, dxr = s.x1 - x;
          const dzl = z - s.z0, dzr = s.z1 - z;
          const m = Math.min(dxl, dxr, dzl, dzr);
          if (m === dxl) x = s.x0 - rad;
          else if (m === dxr) x = s.x1 + rad;
          else if (m === dzl) z = s.z0 - rad;
          else z = s.z1 + rad;
        }
        moved = true;
      }
      if (!moved) break;
    }
    return [x, z];
  }

  /**
   * Can a straight segment from A to B pass without hitting a solid? Slab test per AABB.
   *
   * This is the mechanic that makes "break line of sight" a real escape option (System 6): the
   * robot's eyes are this function, so ducking behind a building genuinely blinds it, and a fence
   * can verify the behaviour by moving the test player rather than toggling a flag.
   */
  lineOfSight(ax, ay, az, bx, by, bz) {
    const dx = bx - ax, dy = by - ay, dz = bz - az;
    for (const s of this.solids) {
      let tmin = 0.02, tmax = 0.98;
      let miss = false;
      for (const [a0, d0, lo, hi] of [[ax, dx, s.x0, s.x1], [ay, dy, s.y0, s.y1], [az, dz, s.z0, s.z1]]) {
        if (Math.abs(d0) < 1e-9) {
          if (a0 < lo || a0 > hi) { miss = true; break; }
        } else {
          let t1 = (lo - a0) / d0, t2 = (hi - a0) / d0;
          if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
          if (t1 > tmin) tmin = t1;
          if (t2 < tmax) tmax = t2;
          if (tmin > tmax) { miss = true; break; }
        }
      }
      if (!miss) return false;
    }
    return true;
  }

  /** Max overlap depth of a capsule (circle in XZ, y-span) into any solid. The creature
   *  world-collision fence reads this: a fence needs a NUMBER, not a boolean. */
  penetration(x, z, rad, y0, y1) {
    let worst = 0;
    for (const s of this.solids) {
      if (y1 <= s.y0 || y0 >= s.y1) continue;
      const nx = Math.max(s.x0, Math.min(x, s.x1));
      const nz = Math.max(s.z0, Math.min(z, s.z1));
      const d = Math.hypot(x - nx, z - nz);
      if (d < rad) worst = Math.max(worst, rad - d);
    }
    return worst;
  }

  stats() {
    return { solids: this.solids.length, floors: this.floors.length, ramps: this.ramps.length };
  }
}
